import React from "react";
import { Search, Users, ChevronLeft, ChevronRight } from "lucide-react";

interface RecipientsSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipientsTotalCount: number;
  recipientsSearchTerm: string;
  setRecipientsSearchTerm: (term: string) => void;
  recipientsItemsPerPage: number;
  handleRecipientsItemsPerPageChange: (itemsPerPage: number) => void;
  availableRecipients: any[];
  selectedRecipientIds: string[];
  onRecipientSelection: (recipient: any, isSelected: boolean) => void;
  filteredAvailableRecipients: any[];
  recipientsLoading: boolean;
  recipientsCurrentPage: number;
  recipientsTotalPages: number;
  handleRecipientsPageChange: (page: number) => void;
  hasLoadedAllRecipients: boolean;
  allRecipients: any[];
  handleSelectAllFromDB: () => void;
  handleSelectAllVisible: () => void;
  handleClearAllSelections: () => void;
  handleLoadAllRecipients: () => void;
  loadingMoreRecipients: boolean;
  selectedAllFromDB: boolean;
  handleAddSelectedRecipients: () => void;
}

const RecipientsSelectionModal: React.FC<RecipientsSelectionModalProps> = ({
  isOpen,
  onClose,
  recipientsTotalCount,
  recipientsSearchTerm,
  setRecipientsSearchTerm,
  recipientsItemsPerPage,
  handleRecipientsItemsPerPageChange,
  availableRecipients,
  selectedRecipientIds,
  onRecipientSelection,
  filteredAvailableRecipients,
  recipientsLoading,
  recipientsCurrentPage,
  recipientsTotalPages,
  handleRecipientsPageChange,
  hasLoadedAllRecipients,
  allRecipients,
  handleSelectAllFromDB,
  handleSelectAllVisible,
  handleClearAllSelections,
  handleLoadAllRecipients,
  loadingMoreRecipients,
  selectedAllFromDB,
  handleAddSelectedRecipients,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        {/* Fixed Header */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-medium text-gray-900">
            Select Recipients ({recipientsTotalCount} total)
            {!hasLoadedAllRecipients &&
              recipientsTotalCount > allRecipients.length && (
                <span className="text-sm text-orange-600 ml-2">
                  (Loaded: {allRecipients.length})
                </span>
              )}
          </h3>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-hidden flex flex-col p-6 pt-4">
          {/* Search and Controls Row */}
          <div className="flex items-center gap-4 mb-4 flex-shrink-0">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search recipients by name or email..."
                value={recipientsSearchTerm}
                onChange={(e) => setRecipientsSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Items Per Page Selector */}
            <div className="flex items-center gap-2 min-w-[120px]">
              <label className="text-sm text-gray-700 whitespace-nowrap">
                Show:
              </label>
              <select
                value={recipientsItemsPerPage}
                onChange={(e) =>
                  handleRecipientsItemsPerPageChange(parseInt(e.target.value))
                }
                className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[80px]"
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
                <option value={0}>All</option>
              </select>
            </div>
          </div>

          {/* Selection Buttons */}
          <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg flex-shrink-0 flex-wrap">
            <button
              onClick={handleSelectAllFromDB}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
            >
              Select All Active (
              {hasLoadedAllRecipients
                ? allRecipients.filter((r) => r.status === "ACTIVE").length
                : `${
                    allRecipients.filter((r) => r.status === "ACTIVE").length
                  }+`}
              )
            </button>
            <button
              onClick={handleSelectAllVisible}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              Select Visible ({availableRecipients.length})
            </button>
            <button
              onClick={handleClearAllSelections}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
            >
              Clear All
            </button>
            {!hasLoadedAllRecipients &&
              recipientsTotalCount > allRecipients.length && (
                <button
                  onClick={handleLoadAllRecipients}
                  disabled={loadingMoreRecipients}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingMoreRecipients ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Loading...
                    </>
                  ) : (
                    `Load All (${
                      recipientsTotalCount - allRecipients.length
                    } more)`
                  )}
                </button>
              )}
            {selectedAllFromDB && (
              <span className="text-sm text-green-600 font-medium">
                All active recipients selected
              </span>
            )}
          </div>

          {recipientsLoading ? (
            <div className="flex items-center justify-center py-12 flex-1">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading recipients...</span>
            </div>
          ) : (
            <>
              {/* Scrollable Recipients List */}
              <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg mb-4 min-h-0">
                <div className="divide-y divide-gray-200">
                  {filteredAvailableRecipients.length > 0 ? (
                    filteredAvailableRecipients.map((recipient) => {
                      const isSelected = selectedRecipientIds.includes(
                        recipient.id
                      );
                      return (
                        <div
                          key={recipient.id}
                          className="p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) =>
                                  onRecipientSelection(
                                    recipient,
                                    e.target.checked
                                  )
                                }
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <div className="ml-3">
                                <div className="flex items-center">
                                  <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <Users className="h-4 w-4 text-blue-600" />
                                  </div>
                                  <div className="ml-3">
                                    <p className="text-sm font-medium text-gray-900">
                                      {recipient.name || "Unknown"}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      {recipient.email}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  recipient.status === "ACTIVE"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {recipient.status || "ACTIVE"}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-12">
                      <Users className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">
                        No recipients found
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        No recipients are available to add to the group.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Pagination Controls */}
              {recipientsItemsPerPage > 0 &&
                recipientsTotalCount > recipientsItemsPerPage && (
                  <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-lg flex-shrink-0">
                    <div className="text-sm text-gray-600">
                      Showing{" "}
                      {(recipientsCurrentPage - 1) * recipientsItemsPerPage + 1}{" "}
                      to{" "}
                      {Math.min(
                        recipientsCurrentPage * recipientsItemsPerPage,
                        recipientsTotalCount
                      )}{" "}
                      of {recipientsTotalCount} recipients
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          handleRecipientsPageChange(recipientsCurrentPage - 1)
                        }
                        disabled={recipientsCurrentPage === 1}
                        className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <span className="text-sm text-gray-600">
                        Page {recipientsCurrentPage} of {recipientsTotalPages}
                      </span>
                      <button
                        onClick={() =>
                          handleRecipientsPageChange(recipientsCurrentPage + 1)
                        }
                        disabled={
                          recipientsCurrentPage === recipientsTotalPages
                        }
                        className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
            </>
          )}
        </div>

        {/* Fixed Footer */}
        <div className="p-6 border-t border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {selectedRecipientIds.length} recipients selected
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSelectedRecipients}
                disabled={selectedRecipientIds.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Selected ({selectedRecipientIds.length})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipientsSelectionModal;
